import { z } from 'zod';
import type { GraphDocument } from '@/domain/funnel/graph-document';
import { FUNNEL_SCHEMA_VERSION } from '@/domain/funnel/schema-version';
import {
  migrateGraphDocumentCopy,
  UnsupportedGraphSchemaError,
} from '@/domain/graph/migrations';
import { NODE_TYPE_IDS } from '@/domain/funnel/node-system';
import { EDGE_TYPE_IDS } from '@/domain/funnel/edge-system';
import {
  collectGraphReferences,
  remapGraphReferences,
  validateGraphReferences,
} from '@/domain/graph/references';
import {
  EXPORT_FORMAT,
  EXPORT_FORMAT_VERSION,
  MAX_IMPORT_BYTES,
  ImportPackageError,
  type DarkFunnelsExportPackage,
  type ImportPreview,
  type ImportWarning,
} from './contracts';
import { PortableGraphSchema } from './graph-schema';
import { safeDataValue } from './safe-value';
import { sanitizeGraphForExport } from './sanitize-export';

const packageSchema = z
  .object({
    format: z.string(),
    formatVersion: z.number().int().positive(),
    schemaVersion: z.number().int().positive(),
    exportedAt: z.string().datetime(),
    metadata: z.object({
      name: z.string().trim().min(1).max(120),
      description: z.string().max(500).nullable().optional(),
    }),
    graph: PortableGraphSchema,
  })
  .strict();

function includesPrivateAssetReferences(value: unknown): {
  attachment: boolean;
  logo: boolean;
} {
  let attachment = false;
  let logo = false;
  function visit(current: unknown) {
    if (!current || typeof current !== 'object') return;
    if (Array.isArray(current)) {
      current.forEach(visit);
      return;
    }
    for (const [key, nested] of Object.entries(current)) {
      if (key === 'attachments' || key === 'storageKey' || key === 'r2Key') attachment = true;
      if (key === 'companyLogoAssetId') logo = true;
      visit(nested);
    }
  }
  visit(value);
  return { attachment, logo };
}

function warningList(rawGraph: GraphDocument, migrated: boolean): ImportWarning[] {
  const warnings = new Set<ImportWarning>();
  const assets = includesPrivateAssetReferences(rawGraph);
  if (assets.attachment) warnings.add('attachments_not_included');
  if (assets.logo) warnings.add('logo_not_included');
  if (rawGraph.nodes.some((node) => !(NODE_TYPE_IDS as readonly string[]).includes(node.type))) {
    warnings.add('unknown_node_type');
  }
  if (rawGraph.edges.some((edge) => !(EDGE_TYPE_IDS as readonly string[]).includes(edge.type))) {
    warnings.add('unknown_edge_type');
  }
  if (migrated) warnings.add('schema_migrated');
  return [...warnings];
}

function validateEnvelope(raw: unknown): DarkFunnelsExportPackage {
  const inert = safeDataValue(raw);
  const parsed = packageSchema.safeParse(inert);
  if (!parsed.success) {
    throw new ImportPackageError(
      'IMPORT_INVALID_GRAPH',
      'The file is not a valid Dark Funnels package.',
    );
  }
  if (parsed.data.format !== EXPORT_FORMAT) {
    throw new ImportPackageError(
      'IMPORT_UNSUPPORTED_FORMAT',
      'Only Dark Funnels export packages can be imported.',
    );
  }
  if (parsed.data.formatVersion !== EXPORT_FORMAT_VERSION) {
    throw new ImportPackageError(
      'IMPORT_UNSUPPORTED_FORMAT_VERSION',
      `Package format version ${parsed.data.formatVersion} is not supported.`,
    );
  }
  if (parsed.data.schemaVersion !== parsed.data.graph.schemaVersion) {
    throw new ImportPackageError(
      'IMPORT_INVALID_GRAPH',
      'Envelope and graph schema versions do not match.',
    );
  }
  return parsed.data as DarkFunnelsExportPackage;
}

function migrateGraphCopy(graph: GraphDocument): { graph: GraphDocument; migrated: boolean } {
  try {
    return {
      graph: migrateGraphDocumentCopy(graph),
      migrated: graph.schemaVersion !== FUNNEL_SCHEMA_VERSION,
    };
  } catch (error) {
    if (!(error instanceof UnsupportedGraphSchemaError)) throw error;
    throw new ImportPackageError(
      'IMPORT_UNSUPPORTED_SCHEMA',
      `Graph schema version ${graph.schemaVersion} is not supported.`,
    );
  }
}

function validateReferences(graph: GraphDocument) {
  const inventory = collectGraphReferences(graph);
  if (inventory.duplicateNodeIds.length || inventory.duplicateEdgeIds.length) {
    throw new ImportPackageError('IMPORT_DUPLICATE_ID', 'The graph contains duplicate IDs.');
  }
  if (inventory.danglingEdgeIds.length) {
    throw new ImportPackageError(
      'IMPORT_DANGLING_EDGE',
      'The graph contains a connection whose source or target is missing.',
    );
  }
  const validation = validateGraphReferences(graph);
  if (!validation.valid) {
    if (validation.errors.some((error) => error.startsWith('Duplicate'))) {
      throw new ImportPackageError('IMPORT_DUPLICATE_ID', validation.errors.join(' '));
    }
    throw new ImportPackageError('IMPORT_INVALID_GRAPH', validation.errors.join(' '));
  }
}

export function parseImportObject(raw: unknown): {
  package: DarkFunnelsExportPackage;
  graph: GraphDocument;
  preview: ImportPreview;
} {
  let byteLength: number;
  try {
    byteLength = new TextEncoder().encode(JSON.stringify(raw)).length;
  } catch {
    throw new ImportPackageError('IMPORT_INVALID_GRAPH', 'The import package is not serializable.');
  }
  if (byteLength > MAX_IMPORT_BYTES) {
    throw new ImportPackageError('IMPORT_FILE_TOO_LARGE', 'The JSON file must be 5 MB or smaller.');
  }
  const envelope = validateEnvelope(raw);
  const migrated = migrateGraphCopy(envelope.graph);
  validateReferences(migrated.graph);
  const warnings = warningList(migrated.graph, migrated.migrated);
  const graph = sanitizeGraphForExport(migrated.graph);
  validateReferences(graph);
  const simulation = graph.simulation as { scenarios?: unknown[] } | undefined;
  return {
    package: { ...envelope, graph },
    graph,
    preview: {
      name: envelope.metadata.name,
      nodeCount: graph.nodes.length,
      edgeCount: graph.edges.length,
      scenarioCount: Array.isArray(simulation?.scenarios) ? simulation.scenarios.length : 0,
      formatVersion: envelope.formatVersion,
      schemaVersion: graph.schemaVersion,
      warnings,
    },
  };
}

export function parseImportText(text: string, byteLength = new TextEncoder().encode(text).length) {
  if (byteLength > MAX_IMPORT_BYTES) {
    throw new ImportPackageError('IMPORT_FILE_TOO_LARGE', 'The JSON file must be 5 MB or smaller.');
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text) as unknown;
  } catch {
    throw new ImportPackageError('IMPORT_INVALID_JSON', 'The selected file is not valid JSON.');
  }
  return parseImportObject(raw);
}

export function prepareImportedGraph(
  raw: unknown,
  options?: { createId?: () => string },
): { graph: GraphDocument; preview: ImportPreview; package: DarkFunnelsExportPackage } {
  const parsed = parseImportObject(raw);
  const remapped = remapGraphReferences(parsed.graph, options).document;
  validateReferences(remapped);
  return { graph: remapped, preview: parsed.preview, package: parsed.package };
}
